import { handleActions } from 'redux-actions';
import { removeFromObject } from '../utilities/common';
import {
  getInitialFromPackage,
  getParametersFromQuote,
  changeUserData,
  updateCalculationJourney,
} from '../utilities/userdata/userDataUtils';
import * as userDataTypes from '../constants/UserData';
import * as apiTypes from '../constants/Api';
import * as types from '../constants/App';

const initialState = {
  loaderProgress: 0,
  packageId: null,
  isCalculatingPrice: false,
  isPurchase: false,
  calculationJourneyStarted: false,
};

const userDataHandler = (state, action) => {
  switch (action.type) {
    case (types.APP_INITIAL): {
      const { payload } = action;
      return {
        ...state,
        ...payload.userData,
      };
    }
    case (userDataTypes.USERDATA_FORM_SUBMIT): {
      const { payload } = action;
      const ignoreValues = ['loaderProgress'];
      const newPayload = removeFromObject(payload, ignoreValues);

      return {
        ...state,
        ...newPayload,
      };
    }
    case (userDataTypes.USERDATA_UPDATE_LOADER): {
      const { payload } = action;
      let { loaderProgress } = state;

      // Add up the payload to the current progress
      loaderProgress += payload;
      // If loaderProgress is higher than 0.97, then set to 1
      loaderProgress = loaderProgress >= 0.97 ? 1 : loaderProgress;

      return {
        ...state,
        loaderProgress,
      };
    }
    case (userDataTypes.USERDATA_RESET_LOADER): {
      return {
        ...state,
        loaderProgress: 0,
      };
    }
    case (userDataTypes.USERDATA_INITIAL): {
      const { payload } = action;
      return {
        ...state,
        ...getInitialFromPackage(payload),
      };
    }
    case (userDataTypes.USERDATA_CHANGE): {
      const { payload } = action;
      return changeUserData(payload, state);
    }
    case (userDataTypes.CALCULATION_JOURNEY_STARTED): {
      const newPayload = updateCalculationJourney(state);
      return {
        ...state,
        ...newPayload,
      };
    }
    default:
      return {};
  }
};

const apiHandler = (state, action) => {
  switch (action.type) {
    case (apiTypes.OAUTH_REQUEST): {
      return {
        ...state,
        isFetchingOauth: true,
      };
    }
    case (apiTypes.OAUTH_RECEIVE): {
      const {
        payload: {
          access_token, /* eslint-disable-line */
          expires_in, /* eslint-disable-line */
          token_type, /* eslint-disable-line */
        },
      } = action;
      return {
        ...state,
        token: {
          access_token,
          expires_in,
          token_type,
          received: Date.now(),
        },
        isFetchingOauth: false,
      };
    }
    case (apiTypes.SERVICES_LOADING): {
      return {
        ...state,
        servicesRunning: true,
      };
    }
    case (apiTypes.SERVICES_LOADED): {
      return {
        ...state,
        servicesRunning: false,
      };
    }
    case (apiTypes.CUSTOMER_CREATE): {
      return {
        ...state,
        isUpdatingCustomer: true,
        // Allow for check if car is not part of the flow.
        noCar: state.basketList.indexOf('car') === -1,
      };
    }
    case (apiTypes.CUSTOMER_CREATED): {
      const { payload } = action;
      return {
        ...state,
        user: {
          ...payload,
        },
        isUpdatingCustomer: false,
      };
    }
    case (apiTypes.CUSTOMER_UPDATE): {
      return {
        ...state,
        isUpdatingCustomer: true,
      };
    }
    case (apiTypes.CUSTOMER_UPDATED): {
      const { payload } = action;
      return {
        ...state,
        user: {
          ...state.user,
          ...payload,
        },
        isUpdatingCustomer: false,
      };
    }
    case (apiTypes.CUSTOMER_GET_ACTIVATION_DATE): {
      return {
        ...state,
        isUpdatingCustomer: true,
      };
    }
    case (apiTypes.CUSTOMER_RECEIVED_ACTIVATION_DATE): {
      const { payload } = action;
      return {
        ...state,
        ...payload,
        isUpdatingCustomer: false,
      };
    }
    case (apiTypes.CUSTOMER_QUOTE): {
      return {
        ...state,
        isUpdatingCustomer: true,
        isCalculatingPrice: true,
      };
    }
    case (apiTypes.CUSTOMER_QUOTED): {
      const { payload } = action;
      let formatPrice = '';
      let params = '';
      let defaults = {};
      if (Object.hasOwnProperty.call(payload, 'totalOfferedPrice')) {
        const { totalOfferedPrice: { priceWithFee } } = payload;
        formatPrice = priceWithFee;
        params = getParametersFromQuote(payload, state);
        params.paymentFrequency = params['paymentFrequency-content'];
        defaults = getParametersFromQuote(payload, state, null, true);
        defaults.price = formatPrice;
      } else {
        Object.keys(payload).forEach((packageId) => {
          params = getParametersFromQuote(payload[packageId], state, packageId);
          const def = getParametersFromQuote(payload[packageId], state, packageId, true);
          def.usedParams = payload[packageId].usedParams;
          // Always add conditional coverages from package as the userData can change the coverages.
          if (state.packages && state.coverageList) {
            const pack = state.packages.filter(p => p.id === packageId);
            def.usedParams.coverageCodes = pack[0].coverages.reduce((result, coverage) => {
              if (state.coverageList[coverage.code] &&
                state.coverageList[coverage.code].conditions &&
                !result.includes(coverage.code)) {
                result.push(coverage.code);
              }
              return result;
            }, def.usedParams.coverageCodes);
          }
          defaults[packageId] = { ...def };
        });
      }

      return {
        ...state,
        price: formatPrice,
        quotes: {
          ...state.user,
          ...payload,
          defaults,
          configurables: defaults,
        },
        ...params,
        isUpdatingCustomer: false,
        isCalculatingPrice: false,
      };
    }
    case (apiTypes.UPDATE_QUOTE): {
      return {
        ...state,
        isUpdatingCustomer: true,
        isCalculatingPrice: true,
      };
    }
    case (apiTypes.UPDATED_QUOTE): {
      const { payload } = action;
      const { quotes } = state;
      let params = '';
      let formatPrice = '';
      if (Object.hasOwnProperty.call(payload, 'totalOfferedPrice')) {
        const { totalOfferedPrice: { priceWithFee }, usedParams } = payload;
        formatPrice = priceWithFee;
        params = getParametersFromQuote(payload, state);
        if (!usedParams.policyID) {
          // Update standard default values.
          quotes.defaults = getParametersFromQuote(payload, state, null, true);
          quotes.defaults.price = formatPrice;
        } else {
          // Use configurables as temporary data storage until user has decided save changes.
          quotes.configurables = getParametersFromQuote(payload, state, null, true);
          quotes.configurables.price = formatPrice;
        }
      } else {
        Object.keys(payload).forEach((packageId) => {
          params = getParametersFromQuote(payload[packageId], state, packageId);
          const defaults = getParametersFromQuote(payload[packageId], state, packageId, true);
          const genericDefault = quotes.defaults[packageId];
          const { usedParams } = quotes.defaults[packageId];
          const packagePayload = payload[packageId].usedParams;
          quotes.defaults[packageId] = { ...genericDefault, ...defaults };
          quotes.defaults[packageId].usedParams = { ...usedParams, ...packagePayload };
        });
      }

      return {
        ...state,
        price: formatPrice,
        quotes: {
          ...quotes,
          ...state.user,
          ...payload,
        },
        ...params,
        isUpdatingCustomer: false,
        isCalculatingPrice: false,
      };
    }
    case (apiTypes.ACCIDENT_CREATE): {
      return {
        ...state,
        isUpdatingAccident: true,
      };
    }
    case (apiTypes.ACCIDENT_CREATED): {
      const { payload } = action;
      return {
        ...state,
        accident: {
          ...payload,
        },
        isUpdatingAccident: false,
      };
    }
    case (apiTypes.ACCIDENT_UPDATE): {
      return {
        ...state,
        isUpdatingAccident: true,
      };
    }
    case (apiTypes.ACCIDENT_UPDATED): {
      const { payload } = action;
      return {
        ...state,
        accident: {
          ...state.accident,
          ...payload,
        },
        isUpdatingAccident: false,
      };
    }
    case (apiTypes.ACCIDENT_PREPARE): {
      return {
        ...state,
        isUpdatingAccident: true,
      };
    }
    case (apiTypes.ACCIDENT_PREPARED): {
      const { payload } = action;
      return {
        ...state,
        accident: {
          ...state.accident,
          ...payload,
        },
        isUpdatingAccident: false,
      };
    }
    case (apiTypes.PERSONAL_CREATE): {
      return {
        ...state,
        isUpdatingPersonal: true,
      };
    }
    case (apiTypes.PERSONAL_CREATED): {
      const { payload } = action;
      return {
        ...state,
        personal: {
          ...payload,
        },
        isUpdatingPersonal: false,
      };
    }
    case (apiTypes.PERSONAL_UPDATE): {
      return {
        ...state,
        isUpdatingPersonal: true,
      };
    }
    case (apiTypes.PERSONAL_UPDATED): {
      const { payload } = action;
      return {
        ...state,
        personal: {
          ...state.personal,
          ...payload,
        },
        isUpdatingPersonal: false,
      };
    }
    case (apiTypes.PERSONAL_PREPARE): {
      return {
        ...state,
        isUpdatingPersonal: true,
      };
    }
    case (apiTypes.PERSONAL_PREPARED): {
      const { payload } = action;
      return {
        ...state,
        personal: {
          ...state.personal,
          ...payload,
        },
        isUpdatingPersonal: false,
      };
    }
    case (apiTypes.CAR_REGISTRATION_NUMBER_VALID): {
      const { payload } = action;
      return {
        ...state,
        ...payload,
        isUpdatingCar: true,
      };
    }
    case (apiTypes.CAR_REGISTRATION_NUMBER_VALIDATED): {
      const { payload } = action;
      let newState = state;
      if (!payload.registrationNumberValid) {
        // Delete vehicle details if the registration number is not valid.
        newState = removeFromObject(state, ['vehicleMakeId', 'model', 'make', 'modelYear']);
      }
      return {
        ...newState,
        ...payload,
        isUpdatingCar: false,
      };
    }
    case (apiTypes.CAR_CREATE): {
      const { payload: { registrationNumberProvided } } = action;
      return {
        ...state,
        registrationNumberProvided,
        isUpdatingCar: true,
      };
    }
    case (apiTypes.CAR_CREATED): {
      const { payload } = action;
      return {
        ...state,
        car: {
          ...payload,
        },
        isUpdatingCar: false,
      };
    }
    case (apiTypes.CAR_UPDATE): {
      const { payload: { registrationNumberProvided } } = action;
      return {
        ...state,
        registrationNumberProvided: !!registrationNumberProvided,
        isUpdatingCar: true,
      };
    }
    case (apiTypes.CAR_UPDATED): {
      const { payload } = action;
      return {
        ...state,
        car: {
          ...state.car,
          ...payload,
        },
        isUpdatingCar: false,
      };
    }
    case (apiTypes.CAR_PREPARE): {
      return {
        ...state,
        isUpdatingCar: true,
      };
    }
    case (apiTypes.CAR_PREPARED): {
      const { payload } = action;
      return {
        ...state,
        car: {
          ...state.car,
          ...payload,
        },
        isUpdatingCar: false,
      };
    }
    case (apiTypes.CONTENT_CREATE): {
      return {
        ...state,
        isUpdatingContent: true,
      };
    }
    case (apiTypes.CONTENT_CREATED): {
      const { payload } = action;
      return {
        ...state,
        content: {
          ...payload,
        },
        isUpdatingContent: false,
      };
    }
    case (apiTypes.CONTENT_UPDATE): {
      return {
        ...state,
        isUpdatingContent: true,
      };
    }
    case (apiTypes.CONTENT_UPDATED): {
      const { payload } = action;
      return {
        ...state,
        content: {
          ...state.content,
          ...payload,
        },
        isUpdatingContent: false,
      };
    }
    case (apiTypes.CONTENT_PREPARE): {
      return {
        ...state,
        isUpdatingContent: true,
      };
    }
    case (apiTypes.CONTENT_PREPARED): {
      const { payload } = action;
      return {
        ...state,
        content: {
          ...state.content,
          ...payload,
        },
        isUpdatingContent: false,
      };
    }
    case (apiTypes.HOUSE_CREATE): {
      return {
        ...state,
        isUpdatingHouse: true,
      };
    }
    case (apiTypes.HOUSE_CREATED): {
      const { payload } = action;
      return {
        ...state,
        house: {
          ...payload,
        },
        isUpdatingHouse: false,
      };
    }
    case (apiTypes.HOUSE_UPDATE): {
      return {
        ...state,
        isUpdatingHouse: true,
      };
    }
    case (apiTypes.HOUSE_UPDATED): {
      const { payload } = action;
      return {
        ...state,
        house: {
          ...state.house,
          ...payload,
        },
        isUpdatingHouse: false,
      };
    }
    case (apiTypes.HOUSE_PREPARE): {
      return {
        ...state,
        isUpdatingHouse: true,
      };
    }
    case (apiTypes.HOUSE_PREPARED): {
      const { payload } = action;
      return {
        ...state,
        house: {
          ...state.house,
          ...payload,
        },
        isUpdatingHouse: false,
      };
    }
    case (apiTypes.PURCHASE): {
      return {
        ...state,
        isUpdatingCustomer: true,
        isPurchase: true,
      };
    }
    case (apiTypes.PURCHASED): {
      const { payload } = action;
      return {
        ...state,
        ...payload,
        isUpdatingCustomer: false,
        isPurchase: false,
      };
    }
    case (apiTypes.SAVE_OFFER): {
      return {
        ...state,
        isUpdatingCustomer: true,
      };
    }
    case (apiTypes.SAVED_OFFER): {
      const { payload } = action;
      return {
        ...state,
        ...payload,
        isUpdatingCustomer: false,
      };
    }
    case (apiTypes.RETRIEVE_OFFER): {
      return {
        ...state,
        isUpdatingCustomer: true,
        isCalculatingPrice: true,
      };
    }
    case (apiTypes.RETRIEVED_OFFER): {
      const { payload } = action;
      const { subProduct } = state;
      let formatPrice = '';
      let chosenPackage = '';
      let product = '';
      let policy = {};
      if (Object.hasOwnProperty.call(payload, 'priceInfo')) {
        const { priceInfo: { totalOfferedPrice: { priceWithFee } } } = payload;
        formatPrice = priceWithFee;
      }

      const productData = Object.keys(payload.productData).reduce((res, elem) => {
        res[elem] = {};
        res[elem].usedParams = payload.productData[elem];
        res[elem].paymentFrequency = payload.productData[elem].paymentFrequency;
        chosenPackage = payload.productData[elem].chosenPackage || elem;
        policy = payload.productData[elem];
        product = elem;

        if (policy.trygDrive === true) {
          policy.coverageCodes.push('_trygdrive');
          subProduct.push('trygdrive');
        }

        return res;
      }, {});

      const agentDetails = payload.agentDetails
        ? {
          agentDetails: payload.agentDetails,
          organizationCode: payload.agentDetails.organizationCode,
        }
        : {};

      return {
        ...state,
        ...payload.customerData,
        ...payload.voucherData,
        ...policy,
        ...agentDetails,
        user: { ...payload.customerData },
        price: formatPrice,
        quotes: {
          ...payload.priceInfo,
          ...payload.productData,
          [chosenPackage]: { ...payload.priceInfo, ...policy },
          [product]: { ...payload.priceInfo, ...policy },
          defaults: { ...productData, [chosenPackage]: payload.productData[product] },
        },
        [chosenPackage]: { ...policy },
        [product]: { ...policy },
        chosenPackage,
        basketList: [product],
        isUpdatingCustomer: false,
        isCalculatingPrice: false,
      };
    }
    case (apiTypes.TRAVEL_CREATE): {
      return {
        ...state,
        isUpdatingTravel: true,
      };
    }
    case (apiTypes.TRAVEL_CREATED): {
      const { payload } = action;
      return {
        ...state,
        travel: {
          ...payload,
        },
        isUpdatingTravel: false,
      };
    }
    case (apiTypes.TRAVEL_UPDATE): {
      return {
        ...state,
        isUpdatingTravel: true,
      };
    }
    case (apiTypes.TRAVEL_UPDATED): {
      const { payload } = action;
      return {
        ...state,
        travel: {
          ...state.travel,
          ...payload,
        },
        isUpdatingTravel: false,
      };
    }
    case (apiTypes.TRAVEL_PREPARE): {
      return {
        ...state,
        isUpdatingTravel: true,
      };
    }
    case (apiTypes.TRAVEL_PREPARED): {
      const { payload } = action;
      return {
        ...state,
        travel: {
          ...state.travel,
          ...payload,
        },
        isUpdatingTravel: false,
      };
    }
    case (apiTypes.INSURANCE_CHECK_CREATE): {
      return {
        ...state,
        isUpdatingInsurance: true,
      };
    }
    case (apiTypes.INSURANCE_CHECK_CREATED): {
      const { payload } = action;
      return {
        ...state,
        recommendation: payload,
        isUpdatingInsurance: false,
      };
    }
    case (apiTypes.INSURANCE_RECOMMENDATION_SENT): {
      const { payload } = action;
      return {
        ...state,
        leadStatus: payload.sent ? payload.sent.toString() : 'false',
      };
    }
    case (apiTypes.DOG_CREATE): {
      return {
        ...state,
        isUpdatingDog: true,
      };
    }
    case (apiTypes.DOG_CREATED): {
      const { payload } = action;
      return {
        ...state,
        dog: {
          ...payload,
        },
        isUpdatingDog: false,
      };
    }
    case (apiTypes.DOG_UPDATE): {
      return {
        ...state,
        isUpdatingDog: true,
      };
    }
    case (apiTypes.DOG_UPDATED): {
      const { payload } = action;
      return {
        ...state,
        dog: {
          ...state.dog,
          ...payload,
        },
        isUpdatingDog: false,
      };
    }
    case (apiTypes.DOG_PREPARE): {
      return {
        ...state,
        isUpdatingDog: true,
      };
    }
    case (apiTypes.DOG_PREPARED): {
      const { payload } = action;
      return {
        ...state,
        dog: {
          ...state.dog,
          ...payload,
        },
        isUpdatingDog: false,
      };
    }
    case (apiTypes.CAT_CREATE): {
      return {
        ...state,
        isUpdatingCat: true,
      };
    }
    case (apiTypes.CAT_CREATED): {
      const { payload } = action;
      return {
        ...state,
        cat: {
          ...payload,
        },
        isUpdatingCat: false,
      };
    }
    case (apiTypes.CAT_UPDATE): {
      return {
        ...state,
        isUpdatingCat: true,
      };
    }
    case (apiTypes.CAT_UPDATED): {
      const { payload } = action;
      return {
        ...state,
        cat: {
          ...state.cat,
          ...payload,
        },
        isUpdatingCat: false,
      };
    }
    case (apiTypes.CAT_PREPARE): {
      return {
        ...state,
        isUpdatingCat: true,
      };
    }
    case (apiTypes.CAT_PREPARED): {
      const { payload } = action;
      return {
        ...state,
        cat: {
          ...state.cat,
          ...payload,
        },
        isUpdatingCat: false,
      };
    }
    default:
      return {};
  }
};

const UserData = handleActions({
  [types.APP_INITIAL]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [userDataTypes.USERDATA_INITIAL]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [userDataTypes.USERDATA_FORM_SUBMIT]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [userDataTypes.USERDATA_UPDATE_LOADER]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [userDataTypes.USERDATA_RESET_LOADER]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [userDataTypes.USERDATA_CHANGE]: (state, action) => ({
    ...userDataHandler(state, action),
  }),
  [userDataTypes.CALCULATION_JOURNEY_STARTED]: (state, action) => ({
    ...state,
    ...userDataHandler(state, action),
  }),
  [apiTypes.OAUTH_REQUEST]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.OAUTH_RECEIVE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.SERVICES_LOADING]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.SERVICES_LOADED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_GET_ACTIVATION_DATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_RECEIVED_ACTIVATION_DATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_QUOTE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CUSTOMER_QUOTED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.UPDATE_QUOTE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.UPDATED_QUOTE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.ACCIDENT_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.ACCIDENT_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.ACCIDENT_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.ACCIDENT_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.ACCIDENT_PREPARE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.ACCIDENT_PREPARED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PERSONAL_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PERSONAL_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PERSONAL_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PERSONAL_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PERSONAL_PREPARE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PERSONAL_PREPARED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_REGISTRATION_NUMBER_VALID]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_REGISTRATION_NUMBER_VALIDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_PREPARE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAR_PREPARED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CONTENT_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CONTENT_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CONTENT_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CONTENT_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CONTENT_PREPARE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CONTENT_PREPARED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSE_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSE_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSE_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSE_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSE_PREPARE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.HOUSE_PREPARED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PURCHASE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.PURCHASED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.SAVE_OFFER]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.SAVED_OFFER]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.RETRIEVE_OFFER]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.RETRIEVED_OFFER]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.TRAVEL_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.TRAVEL_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.TRAVEL_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.TRAVEL_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.TRAVEL_PREPARE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.TRAVEL_PREPARED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.INSURANCE_CHECK_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.INSURANCE_CHECK_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.INSURANCE_RECOMMENDATION_SENT]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.DOG_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.DOG_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.DOG_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.DOG_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.DOG_PREPARE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.DOG_PREPARED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAT_CREATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAT_CREATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAT_UPDATE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAT_UPDATED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAT_PREPARE]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
  [apiTypes.CAT_PREPARED]: (state, action) => ({
    ...state,
    ...apiHandler(state, action),
  }),
}, initialState);

export default UserData;


// WEBPACK FOOTER //
// ./src/reducers/UserData.js